import type { RefObject } from 'react';
import { fadeFromTo } from '../../../util/animation/fade/fade';
import { findTransitionController } from '../../../util/core-transition-component';
import type { ServiceRef } from './service/Service.data';
import type { LottieViewRef } from '../../atoms/lottie-view/LottieView.data';

interface ServicesRefs {
  elementRef?: RefObject<HTMLElement | null>;
  imagesContainerRef?: RefObject<HTMLDivElement | null>;
  imageContainers?: RefObject<Array<RefObject<HTMLDivElement | null>> | null>;
  servicesRef?: RefObject<Array<RefObject<ServiceRef | null>> | null>;
  lottieRef?: RefObject<LottieViewRef | null>;
}

interface ImagesRefs {
  images: RefObject<Array<RefObject<HTMLElement | null>> | null>;
}

export function setupTransitionInTimeline(
  timeline: gsap.core.Timeline,
  { elementRef }: ServicesRefs,
): void {
  if (elementRef?.current) {
    timeline.add(fadeFromTo(elementRef.current), 0);
  }
}

export function imageParallax(timeline: gsap.core.Timeline, { images }: ImagesRefs): void {
  const imageScale = 1.2;
  const distance = (imageScale - 1) * 100;

  images?.current?.forEach((image, index) =>
    timeline.fromTo(
      image.current,
      {
        scale: imageScale,
        yPercent: distance / 2,
      },
      {
        ease: 'none',
        yPercent: -distance / 2,
      },
      index === 0 ? undefined : `<+=${index * 10}%`,
    ),
  );
}

export function setupSlidesTimeline(
  timeline: gsap.core.Timeline,
  { servicesRef }: ServicesRefs,
): void {
  timeline.addLabel(`slide_-1`);

  if (servicesRef) {
    servicesRef?.current?.forEach((service, index) => {
      const serviceController = findTransitionController(service);
      if (serviceController) {
        timeline.add(serviceController.getTimeline('in') as gsap.core.Timeline, '>');
        timeline.addLabel(`slide_${index}`);
        timeline.add(serviceController.getTimeline('out') as gsap.core.Timeline, '>');
      }
    });
  }
}

export function setupImagesTimeline(
  timeline: gsap.core.Timeline,
  { imageContainers }: ServicesRefs,
): void {
  timeline.addLabel(`slide_-1`);

  if (imageContainers?.current) {
    imageContainers?.current.forEach((item, index) => {
      timeline.fromTo(
        item.current,
        { opacity: 0 },
        {
          opacity: 1,
          ease: 'Quad.easeInOut',
          duration: 1,
        },
        '<',
      );
      // timeline.to({}, { duration: 1 });
      timeline.addLabel(`slide_${index}`);
      timeline.to(
        item.current,
        {
          opacity: 0,
          ease: 'Quad.easeInOut',
          duration: 1,
        },
        '>',
      );

      // timeline.to({}, { duration: 1 });

      // if (slideImagesRef?.current) {
      //   timeline.fromTo(
      //     slideImagesRef.current[index],
      //     { scale: 1.5 },
      //     {
      //       scale: 1,
      //       clearProps: 'scale',
      //       ease: 'Quad.easeInOut',
      //       duration: 0.3,
      //     },
      //     '<0.2',
      //   );
      // }
    });
  }
}

export function setupLottieTimeline(
  timeline: gsap.core.Timeline,
  { lottieRef }: ServicesRefs,
): void {
  // timeline.addLabel(`slide_-1`);
  timeline.addLabel(`slide_0`);

  const lottieAnimation = {
    progress: 0,
  };

  const nbAnimation = 5;

  if (lottieRef?.current?.elementRef?.current) {
    for (let index = 0; index < nbAnimation; index++) {
      timeline.to(
        lottieAnimation,
        {
          progress: (index + 1) / nbAnimation,
          ease: 'none',
          duration: 2,
          onUpdate: () => {
            lottieRef?.current?.setProgress(lottieAnimation.progress);
          },
        },
        '<',
      );
      timeline.addLabel(`slide_${index + 1}`);
      timeline.to(
        lottieAnimation,
        {
          progress: (index - 1) / nbAnimation,
          ease: 'none',
          duration: 2,
        },
        '>',
      );
    }
  }
}
